import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import {
  setDrillDownVillageCode,
  setTableHeading,
  setTalukCode,
  setTableChartId,
} from "../../applicationStatus/store";
import DynamicLoader from "../../Form/FormFields/DynamicLoader";
import {
  Level3TableProps,
  removeZeroColumns,
  convertColumns,
  renderCellValue,
} from "./tableUtils";

const Level3Table: React.FC<Level3TableProps> = ({
  selectorData,
  chartId,
  dispatch: externalDispatch,
  id,
  fetchPopupData,
}) => {
  const dispatch = externalDispatch ?? useDispatch();
  const [globalFilter, setGlobalFilter] = useState("");

  // ── Derive data from Redux selector ──
  const dd = chartId ? (selectorData as any)?.[chartId as any] : null;

  let rows: any[] = [];
  let cols: any[] = [];
  if (Array.isArray(dd)) {
    rows = dd;
  } else if (dd) {
    rows = dd.data?.rows || dd.rows || (Array.isArray(dd.data) ? dd.data : []);
    cols = dd.data?.columns || dd.columns || [];
  }
  const isLoading = chartId
    ? selectorData.loaders[`report/chart/${chartId}`]
    : false;

  // ── Build tanstack columns ──
  const formattedCols = React.useMemo(() => {
    const filtered = removeZeroColumns(rows, cols);
    return convertColumns(filtered);
  }, [dd]);

  const table = useReactTable({
    data: rows,
    columns: formattedCols,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
  });

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <DynamicLoader />
      </div>
    );
  }

  // ── Empty state ──
  if (!dd || rows.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500 font-medium text-sm">
        No village data available
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-green-200 shadow-sm">
      <table className="w-full text-left border-separate border-spacing-0">
        <thead className="bg-green-900 text-slate-100 text-center">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  className="px-5 py-2.5 text-[12px] font-semibold uppercase tracking-wider border-b border-green-700 text-nowrap bg-green-900 bg-clip-padding"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-white text-center hover:bg-green-50 transition-colors"
            >
              {row.getVisibleCells().map((cell, ci) => {
                const accessorKey = (cell.column.columnDef as any).accessorKey;
                const original = row.original as any;
                return (
                  <td
                    key={ci}
                    className="px-4 py-2.5 text-[12px] text-slate-600 cursor-pointer"
                    onClick={async () => {
                      await dispatch(
                        setTableHeading({ id, accessorKey } as any),
                      );
                      if (original?.village_code) {
                        await dispatch(
                          setDrillDownVillageCode(original.village_code),
                        );
                      }
                      // village_code alone doesn't disambiguate the popup query,
                      // so send the row's own taluk/district codes as well -
                      // mirrors the fix applied to the taluk-level popup click.
                      if (original?.taluk_code) {
                        await dispatch(setTalukCode(original.taluk_code));
                      }
                      if (original?.district_code) {
                        await dispatch(setTableChartId(original.district_code));
                      }
                      if (fetchPopupData) fetchPopupData(original, "village");
                    }}
                  >
                    {renderCellValue(cell)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Level3Table;
